import * as NavConstants from 'constants/NavConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as ChatActions from 'llm/chat-library/ChatActions';
import {openExistingChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {getChatTypeForNewAgentChat} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';

import {PlotSuggestion} from './SuggestPlotsConstants';

async function submitPromptToNewChat({
  prompt,
  entryId,
}: {
  prompt: string;
  entryId: string | null;
}): Promise<string> {
  const agentType = AgentType.ASSISTANT;
  const chatType = getChatTypeForNewAgentChat(agentType);
  const contextIds = entryId ? [entryId] : [];

  const {chatSessionId} = await ChatActions.createChat(chatType, {}, agentType);
  ChatActions.setDraftMessage(chatSessionId, {
    status: ChatActions.ChatDraftStatus.DRAFT,
    message: {
      text: prompt,
      selectedFiles: [],
    },
    settings: {
      agentType,
      publicDataEnabled: false,
    },
  });
  ChatActions.submitDraftMessage(chatSessionId, {
    createParams: {contextIds},
    analytics: {messageCount: 0},
  });
  openExistingChat(chatSessionId);
  SidepanelActions.openSection(NavConstants.SECTIONS.AI);

  return chatSessionId;
}

export function openCustomPlotDraftInChat({
  tableName,
  entryId,
  entryName,
}: {
  tableName: string;
  entryId: string | null;
  entryName: string | null;
}): Promise<string> {
  const entryContext = entryName ? ` from entry "${entryName}"` : '';
  const prompt = `I want to create a chart for the data in table "${tableName}"${entryContext}. Please ask me one or two questions to determine what the desired plot should contain then create the appropriate chart.`;
  return submitPromptToNewChat({prompt, entryId});
}

export function buildPromptFromSuggestion({
  suggestion,
  tableName,
  entryName,
}: {
  suggestion: PlotSuggestion;
  tableName: string;
  entryName: string | null;
}): string {
  const entryContext = entryName ? ` in entry "${entryName}"` : '';
  const axisParts: Array<string> = [];
  if (suggestion.xAxis) {
    axisParts.push(`x-axis: ${suggestion.xAxis}`);
  }
  if (suggestion.yAxis) {
    axisParts.push(`y-axis: ${suggestion.yAxis}`);
  }
  const axisClause = axisParts.length > 0 ? ` with ${axisParts.join(' and ')}` : '';

  return `Please create a ${suggestion.chartType} chart titled "${suggestion.title}" using data from the table "${tableName}"${entryContext}${axisClause}. ${suggestion.description}`;
}

export function runSuggestPlotPromptInChat({
  suggestion,
  tableName,
  entryId,
  entryName,
}: {
  suggestion: PlotSuggestion;
  tableName: string;
  entryId: string | null;
  entryName: string | null;
}): Promise<string> {
  const prompt = buildPromptFromSuggestion({suggestion, tableName, entryName});
  return submitPromptToNewChat({prompt, entryId});
}
